import axios from 'axios';
import api, { getStoredToken, setStoredToken, setStoredUser } from './api';
import { AdGroup } from '../types/admin';

export interface MeResponse {
  username: string;
  roles: string[];
  departments: string[];
  full_name?: string;
  email?: string;
}

export interface LoginResponse {
  token: string;
  token_type: string;
  expires_in: number;
  user: MeResponse;
}

export interface ChangePasswordPayload {
  current_password: string;
  password: string;
  password_confirmation: string;
}

/**
 * Path del endpoint de login. Default: `/auth/login-json` (produccion, ADR-014).
 * En modo E2E (`VITE_E2E_LOGIN_PATH` configurado en `.env` del build) se
 * usa el endpoint test-only `/auth/login` que firma JWT con clave RSA
 * efimera y no necesita SSO real. Asi la suite Playwright puede ejercitar
 * el LoginPage real sin levantar el SSO.
 */
const E2E_LOGIN_PATH = (import.meta.env.VITE_E2E_LOGIN_PATH as string | undefined) ?? '';
const LOGIN_ENDPOINT = E2E_LOGIN_PATH || '/auth/login-json';

/**
 * Login stateless (ADR-014). El backend hace proxy al SSO y devuelve
 * el JWT firmado por el SSO. La aplicacion usa autenticacion stateless.
 *
 * Returns:
 *   { token, token_type, expires_in, user: { username, roles, ... } }
 */
export const loginJson = async (username: string, password: string): Promise<LoginResponse> => {
  const { data } = await axios.post<LoginResponse>(`${api.defaults.baseURL}${LOGIN_ENDPOINT}`, {
    username,
    password,
  });
  return data;
};

/**
 * Logout local. Borra JWT y profile de localStorage, limpia React Query.
 * NO hace POST a un endpoint: el SSO es stateless.
 */
export const logout = async (): Promise<void> => {
  setStoredToken(null);
  setStoredUser(null);
};

export const fetchMe = () =>
  api.get<MeResponse>('/auth/me').then(r => r.data);

export const changePassword = (payload: ChangePasswordPayload) =>
  api.post<{ ok: boolean }>('/auth/change-password', payload).then(r => r.data);

export const getAvailableAdGroups = () =>
  api.get<AdGroup[]>('/auth/available-ad-groups').then(r => r.data);

export { getStoredToken, setStoredToken };
